import { useState, useEffect } from 'react';
import { ArrowUpRight, ArrowDownRight, Activity, Target, ShieldAlert, Crosshair, Zap } from 'lucide-react';
import useLivePrice from '../hooks/useLivePrice';

function StructureChart() {
  const [pts, setPts] = useState(() => Array.from({ length: 48 }, (_, i) => 46 + Math.sin(i / 3.4) * 15 + Math.random() * 7));
  useEffect(() => {
    const id = setInterval(() => setPts((p) => {
      const n = [...p.slice(1)];
      n.push(Math.max(14, Math.min(90, p[p.length - 1] + (Math.random() - 0.46) * 8)));
      return n;
    }), 1800);
    return () => clearInterval(id);
  }, []);
  const w = 100, h = 100, step = w / (pts.length - 1);
  const line = pts.map((v, i) => `${i * step},${h - v}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-full">
      <defs>
        <linearGradient id="termfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(214,179,106,0.32)" />
          <stop offset="100%" stopColor="rgba(214,179,106,0)" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${line} ${w},${h}`} fill="url(#termfill)" />
      <polyline points={line} fill="none" stroke="#ffd24a" strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// A realistic simulated trade card with a LIVE BTC price (Binance) + fallback.
export default function TradingTerminal({ compact = false }) {
  const { price, change, live } = useLivePrice('BTCUSDT');
  const [conf, setConf] = useState(87);
  useEffect(() => {
    const id = setInterval(() => setConf((c) => Math.max(72, Math.min(94, Math.round(c + (Math.random() - 0.5) * 3)))), 1800);
    return () => clearInterval(id);
  }, []);
  const up = change >= 0;

  return (
    <div className="panel overflow-hidden" style={{ boxShadow: '0 50px 140px rgba(0,0,0,0.7)' }}>
      <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: 'var(--border)', background: 'var(--bg-2)' }}>
        <div className="flex items-center gap-2.5">
          <span className="dot-pulse" />
          <span className="font-mono text-[12px] text-[var(--muted)]">KALKI TERMINAL · LIVE</span>
        </div>
        <span className="chip"><Activity size={13} className="text-[var(--gold-2)]" /> {live ? 'Live price' : 'Illustrative data'}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3">
        <div className="lg:col-span-2 p-5 border-b lg:border-b-0 lg:border-r" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-[18px] font-bold text-white">BTCUSDT</span>
                <span className="font-mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--gold-soft)', color: 'var(--gold-2)' }}>.PERP</span>
              </div>
              <div className="font-mono text-[26px] font-semibold mt-1 ticker-cell text-white">${price.toLocaleString('en-US', { maximumFractionDigits: price < 100 ? 4 : 1 })}</div>
            </div>
            <span className="flex items-center gap-1 font-mono text-[13px]" style={{ color: up ? 'var(--emerald)' : 'var(--down)' }}>{up ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />} {up ? '+' : ''}{change.toFixed(2)}%</span>
          </div>
          <div className={compact ? 'h-[150px]' : 'h-[190px]'}><StructureChart /></div>
          <div className="flex gap-2 mt-3">
            {['15m', '1H', '4H', '1D', '1W'].map((tf, i) => (
              <span key={tf} className="font-mono text-[11px] px-2.5 py-1 rounded" style={{ background: i === 2 ? 'var(--gold-soft)' : 'transparent', color: i === 2 ? 'var(--gold-2)' : 'var(--muted-2)', border: '1px solid var(--border)' }}>{tf}</span>
            ))}
          </div>
        </div>

        <div className="p-5 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[11px] text-[var(--muted-2)]">AI SIGNAL</span>
            <span className="flex items-center gap-1.5 font-mono text-[12px] font-semibold px-2.5 py-1 rounded-md" style={{ color: 'var(--emerald)', background: 'var(--emerald-soft)' }}><Crosshair size={13} /> LONG</span>
          </div>
          <div className="panel-2 p-3.5">
            <div className="flex items-center justify-between"><span className="font-mono text-[11px] text-[var(--muted-2)]">CONFIDENCE</span><span className="font-display text-[18px] font-bold gold-text">{conf}%</span></div>
            <div className="h-1.5 rounded-full overflow-hidden mt-2" style={{ background: 'rgba(255,255,255,.06)' }}>
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${conf}%`, background: 'var(--gold-grad)' }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2.5 font-mono text-[13px]">
            <Field icon={Target} label="Entry" value="71,180" />
            <Field icon={ShieldAlert} label="Stop" value="69,400" color="var(--down)" />
            <Field icon={Target} label="TP1" value="73,600" color="var(--emerald)" />
            <Field icon={Target} label="TP2" value="76,200" color="var(--emerald)" />
          </div>
          <div className="flex items-center justify-between panel-2 p-3">
            <span className="font-mono text-[11px] text-[var(--muted-2)]">RISK / REWARD</span>
            <span className="font-mono text-[13px] gold-text">1 : 3.1</span>
          </div>
          <button className="btn-emerald rounded-lg h-11 flex items-center justify-center gap-2 text-[14px]"><Zap size={16} /> Execute Trade</button>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, color }) {
  return (
    <div className="panel-2 p-3">
      <div className="flex items-center gap-1.5 text-[10px] text-[var(--muted-2)]"><Icon size={11} /> {label.toUpperCase()}</div>
      <div className="text-[15px] font-semibold mt-1" style={{ color: color || 'var(--white)' }}>${value}</div>
    </div>
  );
}
